// Dates, ages, money and text, formatted one way everywhere.
//
// Reporting days are Pacific (LEAD.md, Standing rules): "12d ago" and "past close" must not
// change at 17:00 Pacific just because UTC rolled over. Salesforce dates are plain calendar days
// and are compared as such, never parsed as local midnight.

import type { SalesforceDate, UtcTimestamp } from '../api/types.ts';

const PACIFIC = 'America/Los_Angeles';
const DAY_MS = 24 * 60 * 60 * 1000;

// Today's calendar date in Pacific, as `YYYY-MM-DD`. `en-CA` formats dates in that order.
export function pacificToday(now: Date): SalesforceDate {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

// Whole days from `earlier` to `later`, both calendar dates, independent of the machine's zone.
export function daysBetween(earlier: SalesforceDate, later: SalesforceDate): number {
  const earlierMs = Date.parse(earlier + 'T00:00:00Z');
  const laterMs = Date.parse(later + 'T00:00:00Z');
  return Math.round((laterMs - earlierMs) / DAY_MS);
}

// A calendar date moved by a number of days, for the fixture and for tests.
export function shiftDate(date: SalesforceDate, days: number): SalesforceDate {
  const shifted = new Date(Date.parse(date + 'T00:00:00Z') + days * DAY_MS);
  return shifted.toISOString().slice(0, 10);
}

const SHORT_DATE = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const LONG_DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
});

// "Oct 31", or "Jan 3 ’27" when it isn't this year, short enough to stay on one line.
// Read as UTC because it is a calendar day.
export function formatDate(date: SalesforceDate, today: SalesforceDate): string {
  const short = SHORT_DATE.format(new Date(date + 'T00:00:00Z'));
  if (date.slice(0, 4) === today.slice(0, 4)) return short;
  return `${short} ’${date.slice(2, 4)}`;
}

// "Thu, Sep 10, 2026": the absolute date a relative one shows on hover.
export function formatLongDate(date: SalesforceDate): string {
  return LONG_DATE.format(new Date(date + 'T00:00:00Z'));
}

// "12d ago", "today", "in 3d".
export function formatAge(date: SalesforceDate, today: SalesforceDate): string {
  const days = daysBetween(date, today);
  if (days === 0) return 'today';
  if (days < 0) return `in ${-days}d`;
  return `${days}d ago`;
}

// A UTC timestamp as Pacific wall-clock time: the masthead's sync stamp, a field change's time.
const PACIFIC_TIME = new Intl.DateTimeFormat('en-US', {
  timeZone: PACIFIC, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

export function formatPacificTime(timestamp: UtcTimestamp): string {
  return PACIFIC_TIME.format(new Date(timestamp)) + ' PT';
}

// --- Money -----------------------------------------------------------------------------------

// "$1.15M", "$420K", "$950", for the tiles and the stage boxes.
// Units are chosen after rounding, so $999,500 reads "$1.00M" rather than "$1000K".
export function formatMoney(amount: number): string {
  const absolute = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (Math.round(absolute / 1e3) >= 1000) return sign + '$' + (absolute / 1e6).toFixed(2) + 'M';
  if (Math.round(absolute) >= 1000) return sign + '$' + Math.round(absolute / 1e3) + 'K';
  return sign + '$' + Math.round(absolute);
}

// "$1,250,000", whole dollars, for the table and the drawer.
export function formatDollars(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  return sign + '$' + Math.round(Math.abs(amount)).toLocaleString('en-US');
}

// Most open opportunities carry $0, which in Salesforce means nobody has entered an amount.
// It shows as "—", like an empty one, rather than as a deal worth nothing.
export function hasAmount(amount: number | null): amount is number {
  return amount !== null && amount !== 0;
}

// --- Text ------------------------------------------------------------------------------------

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
};

// An out-of-range code point would make String.fromCodePoint throw mid-render and blank the
// page, so anything outside Unicode is left as written.
function fromCodePointOrKeep(codePoint: number, match: string): string {
  return Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff
    ? String.fromCodePoint(codePoint) : match;
}

function decodeEntity(match: string, code: string): string {
  if (code.startsWith('#x') || code.startsWith('#X')) return fromCodePointOrKeep(parseInt(code.slice(2), 16), match);
  if (code.startsWith('#')) return fromCodePointOrKeep(Number(code.slice(1)), match);
  return NAMED_ENTITIES[code.toLowerCase()] ?? match;
}

export function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, decodeEntity);
}

// Gong's next steps arrive as HTML: `<br>`-separated `* ` bullets with `&#39;` for quotes.
// This turns them into plain lines of text, never markup, so nothing is ever injected.
export function textLines(raw: string): string[] {
  const withBreaks = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(p|div|li|ul|ol)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '');

  // One line per bullet, without the bullet marker.
  return decodeEntities(withBreaks)
    .split('\n')
    .map(line => line.trim().replace(/^[*•-]\s+/, ''))
    .filter(line => line.length > 0);
}

// The same text on one line, for a table cell.
export function plainText(raw: string): string {
  return textLines(raw).join(' ');
}

// Reps start the next step with the day they wrote it ("9/10 - Sent the quote"), which is also
// what NEXT_STEP_DATE_C holds. The cell shows that date on its own line, so the text drops it.
export function withoutLeadingDate(text: string): string {
  const stripped = text.replace(/^\s*\d{1,2}\/\d{1,2}(\/\d{2,4})?\s*[-–:]?\s*/, '');
  return stripped || text;
}

// Almost every opportunity is named "<Account>; <Type> - <detail>". Beside the account column
// the prefix is noise, so the table shows the rest and keeps the full name for hover.
export function shortOpportunityName(opportunityName: string, accountName: string | null): string {
  if (!accountName) return opportunityName;
  if (!opportunityName.toLowerCase().startsWith(accountName.toLowerCase())) return opportunityName;
  const rest = opportunityName.slice(accountName.length).replace(/^[\s;:,\-–—·|]+/, '');
  return rest || opportunityName;
}

// "Jane Q Rep" as ["Jane", "Q Rep"], for the two-line rep cell.
export function splitName(name: string): [string, string] {
  const [first = '', ...rest] = name.trim().split(/\s+/);
  return [first, rest.join(' ')];
}
